import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { confirmDanger } from "../../utils/confirmDialog";
import { FiTrash2, FiExternalLink } from "react-icons/fi";
import contentReportsService from "../../api/services/contentReportsService";
import { useAbortableAsyncEffect } from "../../hooks/useAbortableAsyncEffect";
import { isRequestAbortError } from "../../utils/isRequestAbortError";

/** Public web app base (post URLs). Set REACT_APP_PUBLIC_WEB_APP_URL in .env for staging/local, e.g. http://localhost:3000 */
const WEB_APP_PUBLIC_BASE = (
  typeof process !== "undefined" && process.env.REACT_APP_PUBLIC_WEB_APP_URL
    ? String(process.env.REACT_APP_PUBLIC_WEB_APP_URL).replace(/\/$/, "")
    : "https://naseebi.com"
);

function postUrlInApp(postId) {
  if (postId == null || !Number.isFinite(Number(postId))) return null;
  return `${WEB_APP_PUBLIC_BASE}/en/post/${Number(postId)}`;
}

function truncate(str, max = 120) {
  if (str == null || str === "") return "—";
  const s = String(str);
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function Avatar({ src, alt }) {
  return (
    <img
      src={src || "https://res.cloudinary.com/dykllybp0/image/upload/v1762959801/default-avatar_x44txl.jpg"}
      alt={alt || ""}
      className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0 bg-gray-100"
    />
  );
}

/** Renders image/video attachments for a comment (table cell or modal). */
function CommentAttachmentPreview({ attachments, compact, borderClassName = "border-gray-200" }) {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  const maxH = compact ? "max-h-24" : "max-h-56";
  return (
    <div className={`mt-1.5 flex flex-wrap gap-1.5 ${compact ? "" : "gap-2"}`}>
      {attachments.map((a, idx) => {
        const url = a?.url;
        if (!url) return null;
        const t = String(a.type || a.mediaType || "image").toLowerCase();
        if (t === "video") {
          return (
            <video
              key={`${url}-${idx}`}
              src={url}
              controls
              muted
              playsInline
              className={`${maxH} rounded-md border ${borderClassName} max-w-full bg-black`}
            />
          );
        }
        return (
          <a
            key={`${url}-${idx}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src={url}
              alt=""
              className={`${maxH} w-auto rounded-md border ${borderClassName} object-contain hover:opacity-95`}
            />
          </a>
        );
      })}
    </div>
  );
}

/** Axios body may expose `reports` at top level or under `data`; rare API quirks return array-like objects. */
function normalizeReportsListPayload(data, pageSize) {
  if (data == null || typeof data !== "object") {
    return { rows: [], total: 0, totalPages: 1 };
  }
  const raw =
    data.reports ??
    data.data?.reports ??
    (Array.isArray(data.data) ? data.data : null);
  let rows = [];
  if (Array.isArray(raw)) {
    rows = raw;
  } else if (raw && typeof raw === "object") {
    rows = Object.values(raw);
  }
  const total = Number(data.total ?? data.data?.total ?? rows.length) || 0;
  const rawTp = data.totalPages ?? data.data?.totalPages;
  const per = Math.max(1, Number(pageSize) || 15);
  const totalPages = Number.isFinite(Number(rawTp))
    ? Math.max(1, Number(rawTp))
    : Math.max(1, Math.ceil(total / per) || 1);
  return { rows, total, totalPages };
}

export default function ContentReports() {
  const [status, setStatus] = useState("");
  const [targetType, setTargetType] = useState("");
  const [reportId, setReportId] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const [deleteReportId, setDeleteReportId] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const fetchGeneration = useRef(0);

  const runListFetch = useCallback(
    async (signal) => {
      const gen = ++fetchGeneration.current;
      setLoading(true);
      try {
        const data = await contentReportsService.list(
          page,
          limit,
          {
            status: status || undefined,
            targetType: targetType || undefined,
            reportId: reportId || undefined,
            userId: userId || undefined,
          },
          signal ? { signal } : {}
        );
        if (gen !== fetchGeneration.current) return;
        const { rows, total: t, totalPages: tp } = normalizeReportsListPayload(data, limit);
        setReports(rows);
        setTotal(t);
        setTotalPages(tp);
      } catch (err) {
        if (isRequestAbortError(err)) return;
        if (gen !== fetchGeneration.current) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load content reports";
        toast.error(msg);
        setReports([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        if (gen === fetchGeneration.current) {
          setLoading(false);
        }
      }
    },
    [page, limit, status, targetType, reportId, userId]
  );

  useAbortableAsyncEffect((signal) => runListFetch(signal), [runListFetch]);

  useEffect(() => {
    if (totalPages >= 1 && page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const handleForceDeleteReport = async (report) => {
    const id = report?.id;
    if (id == null) return;
    const dismissedOnly = report.status === "dismissed";
    const msg = dismissedOnly
      ? "Remove this report row from the database? The reported post or comment stays visible in the app."
      : "Permanently delete this report row. Any post or comment that was blocked but not yet fully removed will be deleted from the app.";
    const ok = await confirmDanger(
      msg,
      dismissedOnly ? "Remove report row?" : "Permanently delete report?"
    );
    if (!ok) return;
    setDeleteReportId(id);
    try {
      const res = await contentReportsService.deleteContentReport(id);
      const apiMsg = res?.message && String(res.message).trim();
      toast.success(
        apiMsg ||
          (dismissedOnly ? "Report removed from database" : "Report and linked content cleaned up")
      );
      await runListFetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeleteReportId(null);
    }
  };

  const handleAction = async (reportId, action) => {
    setActingId(reportId);
    try {
      const adminNotes = notesById[reportId]?.trim() || undefined;
      const res = await contentReportsService.update(reportId, { action, adminNotes });
      const apiMsg = res?.message && String(res.message).trim();
      toast.success(
        apiMsg ||
          (action === "remove"
            ? "Content deleted from app; report marked removed"
            : "Report dismissed")
      );
      setNotesById((prev) => ({ ...prev, [reportId]: "" }));
      await runListFetch();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Action failed";
      toast.error(msg);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-md md:text-2xl font-bold text-gray-800">
              Content reports
            </h2>
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setStatus("");
                setTargetType("");
                setReportId("");
                setUserId("");
              }}
              className="self-start sm:self-auto text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Status</label>
              <select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="dismissed">Dismissed</option>
                <option value="removed">Removed</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Type</label>
              <select
                value={targetType}
                onChange={(e) => {
                  setPage(1);
                  setTargetType(e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">All</option>
                <option value="post">Post</option>
                <option value="comment">Comment</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Report ID</label>
              <input
                type="number"
                min={1}
                placeholder="e.g. 27"
                value={reportId}
                onChange={(e) => {
                  setPage(1);
                  setReportId(e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                User ID
              </label>
              <input
                type="number"
                min={1}
                placeholder="Reporter, post or comment author"
                value={userId}
                onChange={(e) => {
                  setPage(1);
                  setUserId(e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          <strong>Dismiss</strong> marks the report closed — the post or comment <strong>stays visible</strong> in the app.{" "}
          <strong>Delete content</strong> (blocked) hides the reported post or comment from the app; the report is marked <em>removed</em> for records / strikes.{" "}
          <strong>Permanently remove</strong> (trash): for <strong>dismissed</strong> or <strong>removed</strong> reports it works as before. For <strong>pending</strong> reports, use it only when the post or comment is already gone from the app (orphan report row); otherwise dismiss or delete content first.{" "}
          To <strong>restore a banned user</strong>, use the{" "}
          <Link to="/community-banned-users" className="text-red-800 font-medium hover:underline">
            Banned Users
          </Link>{" "}
          page. Admins and Developers can <strong>force-ban</strong> a user by ID or email from the{" "}
          <Link to="/community-banned-users" className="text-red-800 font-medium hover:underline">
            Banned Users
          </Link>{" "}
          page (<em>Force ban user</em> button).
        </p>

        {loading ? (
          <p className="text-gray-500 py-8 text-center">Loading…</p>
        ) : reports.length === 0 ? (
          <p className="text-gray-500 py-8 text-center">No reports for this filter.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 whitespace-nowrap">ID</th>
                  <th className="px-3 py-2 whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 min-w-[140px]">Reporter</th>
                  <th className="px-3 py-2 min-w-[160px]">Post</th>
                  <th className="px-3 py-2 min-w-[160px]">Comment</th>
                  <th className="px-3 py-2 min-w-[100px]">Reason</th>
                  <th className="px-3 py-2 whitespace-nowrap">Status</th>
                  <th className="px-3 py-2 min-w-[220px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/80 align-top">
                    <td className="px-3 py-2 text-gray-600">{r.id}</td>
                    <td className="px-3 py-2 capitalize">{r.targetType}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-start gap-2">
                        <Avatar
                          src={r.reporter?.imageUrl}
                          alt={`${r.reporter?.firstName || ""} ${r.reporter?.lastName || ""}`}
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {r.reporter?.firstName} {r.reporter?.lastName}
                          </div>
                          <div className="text-xs text-gray-500">ID {r.reporter?.id}</div>
                          <div className="text-xs text-gray-500 break-all">
                            {r.reporter?.email || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500">
                          Post #{r.post?.id}
                          {r.post?.moderationBlocked ? (
                            <span className="ml-1 text-red-600 font-medium">(hidden)</span>
                          ) : null}
                        </span>
                        {r.post?.id && postUrlInApp(r.post.id) ? (
                          <a
                            href={postUrlInApp(r.post.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:underline"
                          >
                            <FiExternalLink className="w-3.5 h-3.5" />
                            Open in app
                          </a>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setPreviewReport(r)}
                          className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline"
                        >
                          Preview
                        </button>
                      </div>
                      <div className="flex items-start gap-2 mb-1">
                        <Avatar
                          src={r.post?.author?.imageUrl}
                          alt="Post author"
                        />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-800">
                            {r.post?.author?.firstName} {r.post?.author?.lastName}
                            <span className="font-normal text-gray-500">
                              {" "}
                              · ID {r.post?.authorId}
                            </span>
                          </div>
                        </div>
                      </div>
                      {truncate(r.post?.text, 200)}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {r.targetType === "comment" && r.comment ? (
                        <>
                          <div className="text-xs text-gray-500 mb-1">
                            Comment #{r.comment.id}
                            {r.comment.moderationBlocked ? (
                              <span className="ml-1 text-red-600 font-medium">
                                (hidden)
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-start gap-2 mb-1">
                            <Avatar
                              src={r.comment?.author?.imageUrl}
                              alt="Comment author"
                            />
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-gray-800">
                                {r.comment?.author?.firstName} {r.comment?.author?.lastName}
                                <span className="font-normal text-gray-500">
                                  {" "}
                                  · ID {r.comment.authorId}
                                </span>
                              </div>
                            </div>
                          </div>
                          {truncate(r.comment.text, 200)}
                          <CommentAttachmentPreview
                            attachments={r.comment.attachments}
                            compact
                          />
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {truncate(r.reason, 80)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.status === "pending"
                            ? "bg-amber-100 text-amber-900"
                            : r.status === "removed"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-2">
                        {r.status === "pending" ? (
                          <>
                            <input
                              type="text"
                              placeholder="Admin notes (optional)"
                              value={notesById[r.id] ?? ""}
                              onChange={(e) =>
                                setNotesById((prev) => ({
                                  ...prev,
                                  [r.id]: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                            />
                            <div className="flex flex-wrap gap-2 items-center">
                              <button
                                type="button"
                                disabled={actingId === r.id}
                                onClick={() => handleAction(r.id, "dismiss")}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
                              >
                                Dismiss
                              </button>
                              <button
                                type="button"
                                disabled={actingId === r.id}
                                onClick={() => handleAction(r.id, "remove")}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                {actingId === r.id ? "…" : "Delete content"}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-gray-500">
                            {r.reviewedAt
                              ? new Date(r.reviewedAt).toLocaleString()
                              : "—"}
                            {r.adminNotes ? (
                              <div className="mt-1 text-gray-600">{r.adminNotes}</div>
                            ) : null}
                          </div>
                        )}
                        <div className="flex items-center pt-1">
                          <button
                            type="button"
                            title={
                              r.status === "pending"
                                ? "Remove this report row. If the post/comment is already gone, the row is deleted; otherwise use Dismiss or Delete content first."
                                : r.status === "dismissed"
                                  ? "Delete only this report row (content stays in the app)"
                                  : r.status === "removed"
                                    ? "Delete report row and remove any remaining linked content from the app"
                                    : "Permanently remove"
                            }
                            disabled={deleteReportId === r.id}
                            onClick={() => handleForceDeleteReport(r)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 border border-red-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            aria-label="Delete report permanently"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                          <span className="text-[10px] text-gray-400 ml-2">
                            Permanently remove
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {previewReport && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-preview-title"
            onClick={() => setPreviewReport(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex justify-between items-start gap-2">
                <h2 id="report-preview-title" className="text-lg font-semibold text-gray-900">
                  Report #{previewReport.id} — context
                </h2>
                <button
                  type="button"
                  onClick={() => setPreviewReport(null)}
                  className="text-sm text-gray-500 hover:text-gray-800 px-2 py-1"
                >
                  Close
                </button>
              </div>
              <div className="p-4 overflow-y-auto text-sm text-gray-800 space-y-4">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Post
                  </div>
                  <p className="whitespace-pre-wrap break-words">
                    {previewReport.post?.text?.trim()
                      ? previewReport.post.text
                      : previewReport.post?.postMissing
                        ? "(Post missing or deleted from database)"
                        : "—"}
                  </p>
                  {previewReport.post?.id && postUrlInApp(previewReport.post.id) ? (
                    <a
                      href={postUrlInApp(previewReport.post.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-red-700 hover:underline"
                    >
                      <FiExternalLink className="w-4 h-4" />
                      Open post in app
                    </a>
                  ) : null}
                </div>
                {previewReport.post?.media?.length ? (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Post media
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {previewReport.post.media.map((m, idx) => {
                        const type = String(m.mediaType || "image").toLowerCase();
                        const url = m.mediaUrl;
                        if (!url) return null;
                        if (type === "video") {
                          return (
                            <video
                              key={`${url}-${idx}`}
                              src={url}
                              controls
                              className="max-h-56 rounded-lg border border-gray-200 max-w-full bg-black"
                            />
                          );
                        }
                        return (
                          <a
                            key={`${url}-${idx}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={url}
                              alt=""
                              className="max-h-56 rounded-lg border border-gray-200 object-cover hover:opacity-95"
                            />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Comments on this post
                  </div>
                  {previewReport.post?.commentsPreview?.length ? (
                    <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {previewReport.post.commentsPreview.map((c) => {
                        const isReported =
                          previewReport.targetType === "comment" &&
                          previewReport.comment?.id === c.id;
                        const author = [c.author?.firstName, c.author?.lastName]
                          .filter(Boolean)
                          .join(" ")
                          .trim();
                        return (
                          <li
                            key={c.id}
                            className={`rounded-lg border p-2.5 text-sm ${
                              isReported
                                ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300"
                                : "border-gray-100 bg-gray-50"
                            }`}
                          >
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-600 mb-1">
                              <span className="font-medium text-gray-800">
                                {author || `User #${c.userId}`}
                              </span>
                              <span>· comment #{c.id}</span>
                              {c.parentCommentId != null ? (
                                <span className="text-gray-500">· reply to #{c.parentCommentId}</span>
                              ) : null}
                              {c.moderationBlocked ? (
                                <span className="text-red-600 font-medium">· hidden (moderation)</span>
                              ) : null}
                              {isReported ? (
                                <span className="text-amber-800 font-semibold">· reported</span>
                              ) : null}
                            </div>
                            <p className="whitespace-pre-wrap break-words text-gray-800">
                              {c.text || "—"}
                            </p>
                            <CommentAttachmentPreview
                              attachments={c.attachments}
                              borderClassName={
                                isReported ? "border-amber-300" : "border-gray-200"
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No comments on this post.</p>
                  )}
                </div>
                {previewReport.targetType === "comment" &&
                previewReport.comment &&
                !previewReport.post?.commentsPreview?.some(
                  (c) => c.id === previewReport.comment.id
                ) ? (
                  <div>
                    <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
                      Reported comment (not in list — may be deleted)
                    </div>
                    <p className="whitespace-pre-wrap break-words rounded-lg border border-amber-200 bg-amber-50/50 p-2">
                      #{previewReport.comment.id}: {previewReport.comment.text || "—"}
                    </p>
                    {Array.isArray(previewReport.comment.attachments) &&
                    previewReport.comment.attachments.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {previewReport.comment.attachments.map((a, idx) => {
                          const url = a?.url;
                          if (!url) return null;
                          const t = String(a.type || a.mediaType || "image").toLowerCase();
                          if (t === "video") {
                            return (
                              <video
                                key={`${url}-${idx}`}
                                src={url}
                                controls
                                className="max-h-56 rounded-lg border border-amber-200 max-w-full bg-black"
                              />
                            );
                          }
                          return (
                            <a
                              key={`${url}-${idx}`}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={url}
                                alt=""
                                className="max-h-56 rounded-lg border border-amber-200 object-contain hover:opacity-95"
                              />
                            </a>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Reason
                  </div>
                  <p className="text-gray-700">{previewReport.reason || "—"}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
